import { ReportHeader } from '../../header/report-header';
import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { DataTablePageEvent } from 'primereact/datatable';
import { formatDate } from '../../../../utils/date-format';
import { CrmChecksSideBarFilter } from './side-bar/crm-checks-side-bar-filter';
import { CrmChecksSideBarFilterType } from './side-bar/crm-checks-side-bar-filter.type';
import { getReportCrmChecksOperation } from './operations/get-report-crm-checks-operation';
import { CrmPaymentChecksReportRow } from '../../../../api/types/reports/crm/crm-payment-checks-report.type';
import {
  CheckStatusType,
  getCheckStatusDisplayText,
} from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { Tag } from 'primereact/tag';

export const ReportCrmChecks = () => {
  const [data, setData] = useState<CrmPaymentChecksReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const [filters, setFilters] = useState<CrmChecksSideBarFilterType>({
    branchIds: [],
    includeUnassignedBranches: true,
    paymentCollectionStartDate: null,
    paymentCollectionEndDate: null,
    checkRepaymentStartDate: null,
    checkRepaymentEndDate: null,
    checkStatuses: [],
    docNumber: null,
    checkReferenceId: null,
  });

  const fetchReportData = async (
    nextFilters: CrmChecksSideBarFilterType,
    newOffset: number = offset,
    newLimit: number = limit,
  ) => {
    setFilters(nextFilters);
    setOffset(newOffset);
    setLimit(newLimit);
    await getReportCrmChecksOperation(
      nextFilters,
      newOffset,
      newLimit,
      setData,
      setTotal,
      setLoading,
    );
  };

  useEffect(() => {
    fetchReportData(filters);
  }, []);

  const handleOnSearch = async (searchFilters: CrmChecksSideBarFilterType) => {
    await fetchReportData(searchFilters, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    fetchReportData(filters, event.first, event.rows);
  };

  const getStatusSeverity = (
    status: CheckStatusType,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case CheckStatusType.CLEARED:
        return 'success';
      case CheckStatusType.DEPOSITED:
        return 'info';
      case CheckStatusType.PENDING:
        return 'warning';
      case CheckStatusType.BOUNCED:
      case CheckStatusType.CANCELLED:
      case CheckStatusType.EXPIRED:
        return 'danger';
      default:
        return 'warning';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח המחאות - לקוחות (CRM)"
        description="דו״ח זה מציג מידע אודות המחאות שנקלטו בתשלומי ה-CRM. הדו״ח מאפשר סינון לפי שלוחה, סטטוס המחאה וטווחי תאריכים."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב אחר המחאות של לקוחות (CRM)' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="accounting"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <CrmChecksSideBarFilter onSearch={handleOnSearch} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}
          >
            סה״כ רשומות: {total.toLocaleString()}
          </div>

          <DataTable
            value={data}
            rowHover
            loading={loading}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="checkId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{ direction: 'rtl' }}
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) =>
                row.branchName?.trim() ? row.branchName : 'לא קיים'
              }
            />
            <Column
              field="crmPaymentId"
              header="מזהה תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="crmAccountId"
              header="מזהה חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountName"
              header="שם חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="paymentAmount"
              header="סכום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) =>
                typeof row.paymentAmount === 'number'
                  ? row.paymentAmount.toFixed(2)
                  : 'חסר'
              }
            />
            <Column
              field="paymentDate"
              header="תאריך תשלום"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) =>
                row.paymentDate ? formatDate(row.paymentDate as any) : 'חסר'
              }
            />
            <Column
              field="bankNumber"
              header="בנק"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="branchNumber"
              header="סניף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="accountNumber"
              header="חשבון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkNumber"
              header="מס׳ המחאה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="checkPaymentDate"
              header="תאריך פירעון"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) =>
                row.checkPaymentDate
                  ? formatDate(row.checkPaymentDate as any)
                  : 'חסר'
              }
            />
            <Column
              field="checkStatus"
              header="סטטוס המחאה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) => (
                <Tag
                  value={getCheckStatusDisplayText(row.checkStatus)}
                  severity={getStatusSeverity(row.checkStatus) as any}
                />
              )}
            />
            <Column
              header="מספר מסמך"
              style={{ textAlign: 'right', width: '140px' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) =>
                row.accountingDocument?.docNumber ?? (
                  <span style={{ color: '#9ca3af' }}>חסר</span>
                )
              }
            />
            <Column
              header="קובץ"
              style={{ textAlign: 'right', width: '100px' }}
              headerStyle={{ textAlign: 'right' }}
              body={(row: CrmPaymentChecksReportRow) =>
                row.accountingDocument?.fileUrl ? (
                  <a
                    href={row.accountingDocument.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: '#2563eb',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <i className="pi pi-external-link" />
                    פתח
                  </a>
                ) : (
                  <span style={{ color: '#9ca3af' }}>חסר</span>
                )
              }
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
